import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { LngLat } from "react-map-gl/mapbox";
import { CircularProgress } from "@mui/material";

import { api, ParametersModelRun } from "@/lib/api";
import {
  resolveChartSeriesLabel,
  resolveTimeSeriesConfigUnit,
  toParameterTimeSeriesChartResultFromModeled,
} from "@/lib/api/parameter-time-series-chart-result.utils";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import { TimeSeriesConfig } from "@/components/graph/graph.types";
import { DimensionValue } from "@/lib/dimensions";
import Stack from "@/components/stack/stack";
import Spacing from "@/components/spacing/spacing";

import {
  MapControlTimeseriesContent,
  useMapTimeseriesFetchRequests,
} from "./map-control-timeseries-content";

interface MapControlTimeseriesProps {
  modelId: string;
  timeSeriesConfigs: TimeSeriesConfig[];
  latlng: LngLat;
  currentTime: DimensionValue;
}

const MapControlTimeseries = ({
  modelId,
  timeSeriesConfigs,
  latlng,
  currentTime,
}: MapControlTimeseriesProps) => {
  const modelRuns = useQuery({
    queryKey: ["parameters", modelId, "model_runs"],
    queryFn: () => unwrapAxiosData(api.parameters.listModelRuns(modelId)),
  });

  const selectedModelRun = useMemo(() => {
    return modelRuns.data?.[0];
  }, [modelRuns]);

  if (!selectedModelRun)
    return (
      <Stack alignItems="center" justifyContent="center">
        <Spacing top={4}>
          <CircularProgress size={24} color="inherit" />
        </Spacing>
      </Stack>
    );

  return (
    <MapControlTimeseriesInner
      modelId={modelId}
      timeSeriesConfigs={timeSeriesConfigs}
      latlng={latlng}
      modelRun={selectedModelRun}
      currentTime={currentTime}
    />
  );
};

const MapControlTimeseriesInner = ({
  modelId,
  timeSeriesConfigs,
  latlng,
  modelRun,
  currentTime,
}: MapControlTimeseriesProps & {
  modelRun: ParametersModelRun;
}) => {
  const fetchRequests = useMapTimeseriesFetchRequests(timeSeriesConfigs);

  const queryKeyPrefix = useMemo(
    () =>
      [
        "parameters",
        modelId,
        "parameters",
        "timeseries",
        modelRun.reference_time,
        latlng.lat,
        latlng.lng,
      ] as const,
    [modelId, modelRun.reference_time, latlng.lat, latlng.lng],
  );

  return (
    <MapControlTimeseriesContent
      timeSeriesConfigs={timeSeriesConfigs}
      currentTime={currentTime}
      fetchRequests={fetchRequests}
      queryKeyPrefix={queryKeyPrefix}
      fetchParameterSeries={async ({ parameterId, unitCode }) => {
        const response = await api.parameters.getModeledParameterTimeSeriesForCoordinates(
          modelId,
          parameterId,
          modelRun.reference_time,
          latlng.lat,
          latlng.lng,
          unitCode,
        );
        return toParameterTimeSeriesChartResultFromModeled(response.data, {
          parameterLabel: resolveChartSeriesLabel(parameterId, timeSeriesConfigs),
          unit: resolveTimeSeriesConfigUnit(parameterId, timeSeriesConfigs),
        });
      }}
    />
  );
};

export default MapControlTimeseries;
